import {
  AppBar, Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Tooltip,
  Collapse
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import {
  AccountCircle,
  CorporateFare,
  Edit,
  Person,
  RemoveRedEye,
  Search,
  SupervisorAccount,
  ExpandLess,
  ExpandMore,
  Diversity3
} from '@mui/icons-material'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { http } from 'interfaces'
import { AccessLevel, SessionRegion, useSessionContext } from 'session'
import './nav-bar.css'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'
import HistoryEduIcon from '@mui/icons-material/HistoryEdu'
import GroupsIcon from '@mui/icons-material/Groups'
import { getFullUsername } from 'utils'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ApartmentIcon from '@mui/icons-material/Apartment'
import { SettingsDialog } from './settings-dialog'

export const NavBar: React.FC = () => {
  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const { logout, sessionData, openNewTab, hasAccessLevel } = useSessionContext()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isLegislativeOpen, setIsLegislativeOpen] = useState(true)
  const [isExecutiveOpen, setIsExecutiveOpen] = useState(true)

  const handleMenu = (event: any): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleLogout = (): void => {
    http.post('/logout/').then(() => {
      logout()
      handleClose()
      navigate('/login/')
    }).catch((error) => { console.log(error) }
    )
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const goToPage = (s: string): void => {
    setIsDrawerOpen(false)
    openNewTab(s)
  }

  const handleSettingsOpen = (): void => {
    setIsSettingsOpen(true)
  }

  const handleSettingsClose = (): void => {
    setIsSettingsOpen(false)
  }

  const isLoggedIn = (): boolean => {
    return Boolean(sessionData?.username)
  }

  const getAccessLevelIcon = (): React.ReactNode | null => {
    switch (sessionData.accessLevel) {
      case AccessLevel.MANAGER:
        return <SupervisorAccount />
      case AccessLevel.EDITOR:
        return <Edit />
      case AccessLevel.VIEWER:
        return <RemoveRedEye />
      default:
        return null
    }
  }

  const getAccessLevelTooltip = (): string => {
    return `${sessionData.accessLevel.charAt(0).toUpperCase() + sessionData.accessLevel.slice(1)} Account`
  }

  const renderButtonListItem = (icon: React.ReactNode, label: string, page: string): React.ReactNode => {
    return (
      <ListItemButton key={label} onClick={() => { goToPage(page) }}>
        <Box sx={{ mr: 1 }}>
          {icon}
        </Box>
        <ListItemText primary={label} />
      </ListItemButton>
    )
  }

  return (<AppBar position="static">
            <Toolbar>
                <IconButton
                    disabled={!isLoggedIn()}
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={() => { setIsDrawerOpen(true) }}
                >
                    <MenuIcon/>
                </IconButton>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" component="div" sx={{ marginRight: 2 }}>Editorial Tools</Typography>
                </Box>
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontSize: '1.5vw', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '82vw', textAlign: 'center' }}>
                        {sessionData?.module_name} {sessionData?.uniqueId ?? ''}
                    </Typography>
                </Box>
                {<div className="usernameIcon">
                  { isLoggedIn() && <Tooltip title={getAccessLevelTooltip()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        {getAccessLevelIcon()}
                      </Box>
                    </Tooltip>
                  }
                    <Typography className="username" variant="h6" component="div">{getFullUsername(sessionData?.username)}</Typography>
                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        disabled={!isLoggedIn()}
                        onClick={handleMenu}
                        color="inherit"
                    >
                        <AccountCircle/>
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right'
                        }}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem onClick={handleLogout}>
                            <Typography textAlign="center">Logout</Typography>
                        </MenuItem>
                        <MenuItem onClick={handleSettingsOpen}>
                            <Typography textAlign="center">Settings</Typography>
                        </MenuItem>
                    </Menu>
                </div>
                }

                <Drawer open={isDrawerOpen} onClose={() => { setIsDrawerOpen(false) }}>
                    <Box sx={{ width: 250 }}>
                    {sessionData.region === 'international' &&
                    <List>
                        {renderButtonListItem(<Search />, 'Search', '/search')}
                        {renderButtonListItem(<Person />, 'People', '/people')}
                        {renderButtonListItem(<CorporateFare />, 'Organizations', '/organizations')}
                      {hasAccessLevel(AccessLevel.MANAGER) &&
                          renderButtonListItem(<ChecklistRtlIcon />, 'SLOs', '/slos')
                      }
                    </List>
                    }
                    {sessionData.region === SessionRegion.US &&
                        <List>
                            {renderButtonListItem(<Search />, 'Search', '/us/search')}
                            {renderButtonListItem(<Person />, 'People', '/us/people')}
                            {hasAccessLevel(AccessLevel.MANAGER) && renderButtonListItem(<ChecklistRtlIcon />, 'PIDF', '/us/pidf')}
                            <ListItemButton onClick={() => { setIsLegislativeOpen(!isLegislativeOpen) }}>
                                <ListItemText primary="Legislative" />
                                {isLegislativeOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                          <Collapse in={isLegislativeOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {renderButtonListItem(<HistoryEduIcon/>, 'Members', '/us/legMember/')}
                              {renderButtonListItem(<Diversity3/>, 'Commitees', '/us/committee')}
                              {renderButtonListItem(<GroupsIcon/>, 'Leadership/Officers', '/us/leadershipOfficers')}
                              {hasAccessLevel(AccessLevel.MANAGER) && renderButtonListItem(<ApartmentIcon/>, 'Buildings', '/us/buildings/')}
                            </List>
                          </Collapse>
                          <ListItemButton onClick={() => { setIsExecutiveOpen(!isExecutiveOpen) }}>
                                <ListItemText primary="Executive" />
                                {isExecutiveOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={isExecutiveOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {renderButtonListItem(<CorporateFare/>, 'Organizations', '/us/executives/orgs')}
                                    {renderButtonListItem(<PeopleOutlineIcon/>, 'Positions', '/us/executives/search')}
                                </List>
                            </Collapse>
                        </List>
                    }
                    </Box>
                </Drawer>
            </Toolbar>
          <SettingsDialog open={isSettingsOpen} onClose={handleSettingsClose} />
        </AppBar>
  )
}
